import type { PageLoaderBranding } from '@/common/components/page-loader';

const INITIAL_APP_LOAD_STATE_KEY = 'initialAppLoad';
const INITIAL_APP_LOAD_STARTED_AT_STATE_KEY = 'initialAppLoadStartedAt';
const INITIAL_APP_LOAD_ADVISOR_NAME_STATE_KEY = 'initialAppLoadAdvisorName';
const INITIAL_APP_LOAD_DESTINATION_STATE_KEY = 'initialAppLoadDestination';
const INITIAL_APP_LOAD_BRANDING_STATE_KEY = 'initialAppLoadBranding';
const INITIAL_APP_LOAD_SUPPRESS_ADVISOR_NAME_STATE_KEY = 'initialAppLoadSuppressAdvisorName';

export type InitialAppLoadDestination = 'dashboard' | 'planner' | 'clients';

type InitialAppLoadLocationState = Record<string, unknown> & {
  [INITIAL_APP_LOAD_STATE_KEY]?: true;
  [INITIAL_APP_LOAD_STARTED_AT_STATE_KEY]?: number;
  [INITIAL_APP_LOAD_ADVISOR_NAME_STATE_KEY]?: string;
  [INITIAL_APP_LOAD_DESTINATION_STATE_KEY]?: InitialAppLoadDestination;
  [INITIAL_APP_LOAD_BRANDING_STATE_KEY]?: PageLoaderBranding;
  [INITIAL_APP_LOAD_SUPPRESS_ADVISOR_NAME_STATE_KEY]?: true;
};

const isLocationStateRecord = (state: unknown): state is Record<string, unknown> =>
  typeof state === 'object' && state !== null && !Array.isArray(state);

export const getInitialAppLoadStartedAt = (state: unknown): number | undefined => {
  if (!isLocationStateRecord(state)) {
    return undefined;
  }

  const startedAt = state[INITIAL_APP_LOAD_STARTED_AT_STATE_KEY];
  return typeof startedAt === 'number' && Number.isFinite(startedAt) ? startedAt : undefined;
};

const getValidStartedAt = (startedAt: number | undefined): number | undefined =>
  typeof startedAt === 'number' && Number.isFinite(startedAt) ? startedAt : undefined;

const getValidAdvisorName = (advisorName: string | undefined): string | undefined => {
  const trimmedName = advisorName?.trim();
  return trimmedName ? trimmedName : undefined;
};

const getValidString = (value: unknown): string | undefined => {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';
  return trimmedValue || undefined;
};

const getValidBranding = (branding: unknown): PageLoaderBranding | undefined => {
  if (!isLocationStateRecord(branding)) {
    return undefined;
  }

  const logoUri = getValidString(branding.logoUri);
  const watermarkLogoUri = getValidString(branding.watermarkLogoUri);

  if (!logoUri && !watermarkLogoUri) {
    return undefined;
  }

  return {
    logoAlt: getValidString(branding.logoAlt),
    logoUri,
    watermarkLogoUri,
  };
};

const INITIAL_APP_LOAD_DESTINATIONS: ReadonlySet<InitialAppLoadDestination> = new Set([
  'dashboard',
  'planner',
  'clients',
]);

const isInitialAppLoadDestination = (value: unknown): value is InitialAppLoadDestination =>
  typeof value === 'string' &&
  INITIAL_APP_LOAD_DESTINATIONS.has(value as InitialAppLoadDestination);

export const getInitialAppLoadAdvisorName = (state: unknown): string | undefined => {
  if (!isLocationStateRecord(state)) {
    return undefined;
  }

  const advisorName = state[INITIAL_APP_LOAD_ADVISOR_NAME_STATE_KEY];
  return typeof advisorName === 'string' && advisorName.trim().length > 0 ? advisorName : undefined;
};

export const getInitialAppLoadSuppressAdvisorName = (state: unknown): boolean =>
  isLocationStateRecord(state) && state[INITIAL_APP_LOAD_SUPPRESS_ADVISOR_NAME_STATE_KEY] === true;

export const getInitialAppLoadDestination = (
  state: unknown,
): InitialAppLoadDestination | undefined => {
  if (!isLocationStateRecord(state)) {
    return undefined;
  }

  const destination = state[INITIAL_APP_LOAD_DESTINATION_STATE_KEY];
  return isInitialAppLoadDestination(destination) ? destination : undefined;
};

export const getInitialAppLoadBranding = (state: unknown): PageLoaderBranding | undefined => {
  if (!isLocationStateRecord(state)) {
    return undefined;
  }

  return getValidBranding(state[INITIAL_APP_LOAD_BRANDING_STATE_KEY]);
};

export const markInitialAppLoadState = (
  state?: unknown,
  startedAt?: number,
  options: {
    advisorName?: string;
    branding?: PageLoaderBranding;
    destination?: InitialAppLoadDestination;
    suppressAdvisorName?: boolean;
  } = {},
): InitialAppLoadLocationState => {
  const existingState = isLocationStateRecord(state) ? state : {};
  const suppressAdvisorName =
    options.suppressAdvisorName ?? getInitialAppLoadSuppressAdvisorName(existingState);
  const advisorName = suppressAdvisorName
    ? undefined
    : (getValidAdvisorName(options.advisorName) ?? getInitialAppLoadAdvisorName(existingState));
  const branding = getValidBranding(options.branding) ?? getInitialAppLoadBranding(existingState);
  const destination = options.destination ?? getInitialAppLoadDestination(existingState);

  const nextState: InitialAppLoadLocationState = {
    ...existingState,
    [INITIAL_APP_LOAD_STATE_KEY]: true,
    [INITIAL_APP_LOAD_STARTED_AT_STATE_KEY]:
      getInitialAppLoadStartedAt(existingState) ?? getValidStartedAt(startedAt) ?? Date.now(),
  };

  if (advisorName) {
    nextState[INITIAL_APP_LOAD_ADVISOR_NAME_STATE_KEY] = advisorName;
  } else {
    delete nextState[INITIAL_APP_LOAD_ADVISOR_NAME_STATE_KEY];
  }

  if (suppressAdvisorName) {
    nextState[INITIAL_APP_LOAD_SUPPRESS_ADVISOR_NAME_STATE_KEY] = true;
  } else {
    delete nextState[INITIAL_APP_LOAD_SUPPRESS_ADVISOR_NAME_STATE_KEY];
  }

  if (destination) {
    nextState[INITIAL_APP_LOAD_DESTINATION_STATE_KEY] = destination;
  }

  if (branding) {
    nextState[INITIAL_APP_LOAD_BRANDING_STATE_KEY] = branding;
  }

  return nextState;
};

export const hasInitialAppLoadState = (state: unknown): boolean =>
  isLocationStateRecord(state) && state[INITIAL_APP_LOAD_STATE_KEY] === true;

export const clearInitialAppLoadState = (state: unknown): Record<string, unknown> | null => {
  if (!isLocationStateRecord(state)) {
    return null;
  }

  const nextState = { ...state };
  delete nextState[INITIAL_APP_LOAD_STATE_KEY];
  delete nextState[INITIAL_APP_LOAD_STARTED_AT_STATE_KEY];
  delete nextState[INITIAL_APP_LOAD_ADVISOR_NAME_STATE_KEY];
  delete nextState[INITIAL_APP_LOAD_DESTINATION_STATE_KEY];
  delete nextState[INITIAL_APP_LOAD_BRANDING_STATE_KEY];
  delete nextState[INITIAL_APP_LOAD_SUPPRESS_ADVISOR_NAME_STATE_KEY];

  return Object.keys(nextState).length > 0 ? nextState : null;
};
